import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(customParseFormat);

export const FORMAT_BY_LOCALE = {
    en: 'MM/DD/YYYY',
    ru: 'DD.MM.YYYY'
};
export const FORMAT_BY_LOCALE_MONTH = {
    en: 'MMMM YYYY',
    ru: 'MMMM YYYY'
};

export const REQUEST_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

export const DATE_FORMAT = 'YYYY-MM';

export function getCurrentDateFormat() {
    return FORMAT_BY_LOCALE[dayjs.locale()];
}

export function formatDate(dateString, format) {
    if (!dateString || !(typeof dateString === 'string') || isNaN(Date.parse(dateString))) return '';
    if (!format) format = FORMAT_BY_LOCALE[dayjs.locale()];

    return dayjs(dateString).format(format);
}

export function getNowDate() {
    return dayjs().set({ second: 0, millisecond: 0 }).format(REQUEST_FORMAT);
}

export function getNowDateFormatWithoutTime() {
    return dayjs().set({ second: 0, millisecond: 0 });
}
export function getNextYearDateFormatWithoutTime() {
    return dayjs().set({ second: 0, millisecond: 0 }).add(1, 'year');
}

export function getNextYear(startDate) {
    if (!startDate || isNaN(Date.parse(startDate))) return '';
    return dayjs(startDate).add(1, 'year').format(REQUEST_FORMAT);
}

export function getNextYearByLocale(startDate) {
    if (!startDate || isNaN(Date.parse(startDate))) return '';
    return dayjs(startDate).add(1, 'year');
}

export function getNextTenCalendarYears() {
    const nextTenYear = new Date().getFullYear() + 10;
    return dayjs(`${nextTenYear}-12-31`);
}

export function getPreviousTenCalendarYears() {
    const previousTenYear = new Date().getFullYear() - 10;
    const date = `01/01/${previousTenYear}`;
    return dayjs(date);
}

export function getPreviousFiveCalendarYear() {
    const previousFiveYear = new Date().getFullYear() - 5;
    const date = `01/01/${previousFiveYear}`;
    return dayjs(date);
}

export function isExpired(months, expDate) {
    return dayjs().add(months, 'month').isAfter(formatDate(expDate, 'YYYY-MM-DD'));
}

export function isCompleteExpired(expDate) {
    return dayjs().isSameOrAfter(formatDate(expDate, 'YYYY-MM-DD'));
}

export function isBeforeDate(comparDate, expDate) {
    return dayjs(comparDate).isBefore(expDate);
}

export function isBeforeDateStrict(comparDate, expDate) {
    return dayjs(formatDate(comparDate, 'YYYY-MM-DD')).isBefore(formatDate(expDate, 'YYYY-MM-DD'));
}

export function isAfterDate(comparDate, expDate) {
    return dayjs(comparDate).isAfter(expDate);
}

export function isAfterDateStrict(comparDate, expDate) {
    return dayjs(formatDate(comparDate, 'YYYY-MM-DD')).isAfter(expDate);
}

export function getMinDate(date) {
    return dayjs(date);
}

export function getMaxDate(date) {
    return dayjs(date);
}

export function isValidDate(date) {
    if (typeof date !== 'string' && typeof date !== 'number') return false;

    const value = String(date).trim();
    if (!value) return false;

    // --- YYYY-MM-DD ---
    const ymd = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
        const [_, y, m, d] = ymd;
        return checkRealDate(+y, +m, +d);
    }

    // --- DD.MM.YYYY ---
    const dmy = value.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (dmy) {
        const [_, d, m, y] = dmy;
        return checkRealDate(+y, +m, +d);
    }

    // --- MM/DD/YYYY ---
    const mdy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (mdy) {
        const [_, m, d, y] = mdy;
        return checkRealDate(+y, +m, +d);
    }

    // --- DD.MM.YYYY HH:mm:ss ---
    const dmyTime = value.match(/^(\d{2})\.(\d{2})\.(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
    if (dmyTime) {
        const [_, d, m, y, hh, mm, ss] = dmyTime;
        return checkRealDateTime(+y, +m, +d, +hh, +mm, +ss);
    }

    // --- MM/DD/YYYY HH:mm:ss ---
    const mdyTime = value.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);
    if (mdyTime) {
        const [_, m, d, y, hh, mm, ss] = mdyTime;
        return checkRealDateTime(+y, +m, +d, +hh, +mm, +ss);
    }

    // --- M/D/YYYY hh:mm:ss AM/PM ---
    const mdyAmPm = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2}) (AM|PM)$/i);
    if (mdyAmPm) {
        const [_, m, d, y, hh, mm, ss, ap] = mdyAmPm;
        let hour = +hh;

        if (ap.toUpperCase() === 'PM' && hour < 12) hour += 12;
        if (ap.toUpperCase() === 'AM' && hour === 12) hour = 0;

        return checkRealDateTime(+y, +m, +d, hour, +mm, +ss);
    }

    // --- ISO: 2024-01-15T12:00:00Z ---
    const iso = value.match(
        /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(?:\.\d{3})?(Z)?)?$/
    );
    if (iso) {
        const d = new Date(value);
        return !Number.isNaN(d.getTime());
    }

    return false;
}

function checkRealDate(year, month, day) {
    const d = new Date(year, month - 1, day);
    return (
        d.getFullYear() === year &&
        d.getMonth() === month - 1 &&
        d.getDate() === day
    );
}

function checkRealDateTime(
    year,
    month,
    day,
    hour,
    minute,
    second
) {
    if (hour < 0 || hour > 23) return false;
    if (minute < 0 || minute > 59) return false;
    if (second < 0 || second > 59) return false;

    const d = new Date(year, month - 1, day, hour, minute, second);

    return (
        d.getFullYear() === year &&
        d.getMonth() === month - 1 &&
        d.getDate() === day &&
        d.getHours() === hour &&
        d.getMinutes() === minute &&
        d.getSeconds() === second
    );
}
